import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChange,
    SimpleChanges,
    ViewEncapsulation
} from "@angular/core";
import {FormControl} from "@angular/forms";
import {ReplaySubject} from "rxjs";
import {Api, ApiService} from "../../../services/api.service";
import {Warehouse} from "../../../interfaces/warehouse.interface";
import {Modal, ModalService} from "../../../../section/services/modal.service";
import {SpinnerService} from "../../../services/spinner.service";
import {debounceTime, distinctUntilChanged, takeUntil} from "rxjs/operators";
import {HubsMapModalComponent} from "../../../../section/components/partner/components/hubs";

interface IHubGroup {
    name: string;
    checked: boolean;
    indeterminate: boolean;
}

@Component({
    selector: "common-form-hub-select",
    template: `
        <div *ngIf="disabled && hubs" class="margin-bottom-10">
            <span class="label">
                {{ label }}
            </span>
            <div>
                {{ getHubNameById(hub.value) }}
            </div>
        </div>

        <mat-form-field *ngIf="!disabled && hubs" style="width: 100%">
            <mat-label>{{ label }}</mat-label>
            <mat-select [formControl]="hub"
                        [multiple]="multiple"
                        [required]="required"
                        (closed)="closed()">

                <mat-select-trigger>
                    {{ getHubNameById(hub.value) }}
                </mat-select-trigger>

                <mat-option>
                    <ngx-mat-select-search [formControl]="hubSearch"
                                           placeholderLabel="Search"
                                           [showToggleAllCheckbox]="selectAll"
                                           (toggleAll)="allSelect()"
                                           noEntriesFoundLabel="no hubs found"></ngx-mat-select-search>
                </mat-option>

                <!--
                    Selected hubs that the current search filters out. mat-select rebuilds its
                    selection model from the rendered options, so without these it would consider
                    itself empty (hiding mat-select-trigger) and drop them from the value on the
                    next selection.
                -->
                <mat-option *ngFor="let option of hiddenSelected" [value]="option.id"
                            [disabled]="true"
                            class="hub-hidden-option"></mat-option>

                <mat-option *ngIf="!required && !multiple" [value]="null">None</mat-option>
                <mat-option *ngIf="!hubs.length" [value]="null">
                    {{ empty_list_message }}
                </mat-option>

                <ng-template [ngIf]="multiple" [ngIfElse]="single">
                    <div *ngFor="let row of hubsFiltered | async">
                        <mat-checkbox class="hub-group"
                                      [checked]="row.group.checked"
                                      [indeterminate]="row.group.indeterminate"
                                      (change)="groupSelectAll(row, $event.checked)">
                            {{ row.group.name }}
                        </mat-checkbox>
                        <mat-option *ngFor="let option of row.hubs" [value]="option.id"
                                    [disabled]="disable_inactive && !option.is_active"
                                    class="{{option.gb}} mat-option group-item">
                            {{ option.customers_sub_inventory }}
                            ({{ option.gb }})
                            <ng-container *ngIf="option.distance">
                                {{ option.distance }}km
                            </ng-container>
                            <ng-container *ngIf="!option.is_active"> | INACTIVE</ng-container>
                        </mat-option>
                    </div>
                </ng-template>

                <ng-template #single>
                    <mat-optgroup *ngFor="let row of hubsFiltered | async" [label]="row.group.name">
                        <mat-option *ngFor="let option of row.hubs" [value]="option.id"
                                    [disabled]="disable_inactive && !option.is_active"
                                    class="{{option.gb}} mat-option group-item">
                            {{ option.customers_sub_inventory }}
                            ({{ option.gb }})
                            <ng-container *ngIf="option.distance">
                                {{ option.distance }}km
                            </ng-container>
                            <ng-container *ngIf="!option.is_active"> | INACTIVE</ng-container>
                        </mat-option>
                    </mat-optgroup>
                </ng-template>

            </mat-select>
            <mat-icon matSuffix (click)="openMap();$event.stopPropagation()">
                place
            </mat-icon>

            <common-form-error [control]="hub" controlName="hub"></common-form-error>
        </mat-form-field>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ["hub-select.component.scss"],
})

export class HubSelectComponent implements OnInit, OnDestroy, OnChanges {

    /**
     * Inputs that make the currently loaded hub list stale and require a reload
     */
    private static readonly RELOAD_ON_CHANGE: string[] = [
        "address_id",
        "line_type",
        "service_level_id",
        "part_master_id",
        "part_master_in_stock"
    ];

    protected destroy$: EventEmitter<boolean> = new EventEmitter(false);

    public hubs: { group: IHubGroup, hubs: Warehouse.IHub[] }[];

    public hub: FormControl = new FormControl(null);

    public hubSearch: FormControl = new FormControl(null);

    public hubsFiltered: ReplaySubject<any> = new ReplaySubject(1);

    /**
     * Selected hubs that are not part of the current search result
     */
    public hiddenSelected: Warehouse.IHub[] = [];

    @Input()
    public options: { [key: string]: Warehouse.IHub[] } = null;

    @Input()
    public options_grouped: { group: string, hubs: Warehouse.IHub[] }[] = null;

    @Input()
    public value: number = null;

    @Input()
    public url: string = null;

    @Input()
    public address_id: number = null;

    @Input()
    public line_type: string = null;

    @Input()
    public label: string = "Hub";

    @Input()
    public service_level_id: number = null;

    @Input()
    public part_master_id: number = null;

    @Input()
    public part_master_in_stock: boolean = false;

    @Input()
    public required: boolean = false;

    @Input()
    public disabled: boolean = false;

    @Input()
    public multiple: boolean = false;

    @Input()
    public selectAll: boolean = false;

    @Input()
    public hideSingles: boolean = false;

    @Input()
    public empty_list_message: string = "Hubs not found";

    @Input()
    public show_inactive: boolean = false;

    @Input()
    public disable_inactive: boolean = true;

    @Output()
    public onChange: EventEmitter<any> = new EventEmitter<any>();

    @Output()
    public onClose: EventEmitter<any> = new EventEmitter<any>();

    @Output()
    public onValueName: EventEmitter<any> = new EventEmitter<any>();

    @Output()
    public onGetHubs: EventEmitter<any> = new EventEmitter<any>();

    @Output()
    public onGroupSelected: EventEmitter<{ group: string, hubs: Warehouse.IHub[] }[]> =
        new EventEmitter<{ group: string, hubs: Warehouse.IHub[] }[]>();


    public constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private apiService: ApiService,
        private modalService: ModalService,
        private spinnerService: SpinnerService
    ) {

    }

    /**
     * Map hubs
     * @param hubs
     */
    private mapHubs(hubs: { [key: string]: Warehouse.IHub[] }): { group: IHubGroup, hubs: Warehouse.IHub[] }[] {
        const mappedHubs: { group: IHubGroup, hubs: Warehouse.IHub[] }[] = [];
        for (const group_name of Object.keys(hubs)) {
            if (this.hideSingles && hubs[group_name].length < 2) {
                continue;
            }
            mappedHubs.push({
                group: {
                    name: group_name,
                    checked: false,
                    indeterminate: false
                },
                hubs: hubs[group_name]
            });
        }
        return mappedHubs;
    }

    /**
     * Map already grouped hubs (as provided via options_grouped)
     * @param options
     */
    private mapGroupedHubs(
        options: { group: string, hubs: Warehouse.IHub[] }[]
    ): { group: IHubGroup, hubs: Warehouse.IHub[] }[] {
        return options.map((option: { group: string, hubs: Warehouse.IHub[] }) => {
            return {
                group: {
                    name: option.group,
                    checked: false,
                    indeterminate: false
                },
                hubs: option.hubs
            };
        });
    }

    private filterHubs(hubsList: { group: IHubGroup, hubs: Warehouse.IHub[] }[], value: string): any {
        value = value.toLowerCase();
        const filtered: { group: IHubGroup, hubs: Warehouse.IHub[] }[] = [];
        for (const row of hubsList) {
            if (row.group && row.group.name.toLowerCase().includes(value)) {
                filtered.push(row);
            } else {
                const hubs: Warehouse.IHub[] = [];
                for (const hub of row.hubs) {
                    if (hub.customers_sub_inventory && hub.customers_sub_inventory.toLowerCase().includes(value)) {
                        hubs.push(hub);
                    }
                }
                if (hubs.length) {
                    filtered.push({group: row.group, hubs});
                }
            }
        }
        return filtered;
    }

    /**
     * Publish the hub list matching the given search
     * @param search
     */
    private applySearch(search: string): void {
        const filtered: { group: IHubGroup, hubs: Warehouse.IHub[] }[] =
            search ? this.filterHubs(this.hubs, search) : this.hubs;

        this.hiddenSelected = this.getSelectedOutOf(filtered);
        this.hubsFiltered.next(filtered);
        this.changeDetectorRef.markForCheck();
    }

    /**
     * Selected hubs that are missing from the given (filtered) list
     * @param visibleHubs
     */
    private getSelectedOutOf(
        visibleHubs: { group: IHubGroup, hubs: Warehouse.IHub[] }[]
    ): Warehouse.IHub[] {
        const ids: number[] = this.getSelectedIds();

        if (!ids.length) {
            return [];
        }

        const visibleIds: Set<number> = new Set<number>();
        for (const row of visibleHubs) {
            for (const hub of row.hubs) {
                visibleIds.add(hub.id);
            }
        }

        const hidden: Map<number, Warehouse.IHub> = new Map<number, Warehouse.IHub>();
        for (const row of this.hubs) {
            for (const hub of row.hubs) {
                if (ids.includes(hub.id) && !visibleIds.has(hub.id)) {
                    hidden.set(hub.id, hub);
                }
            }
        }

        return [...hidden.values()];
    }

    /**
     * Currently selected hub ids, regardless of single/multiple mode
     */
    private getSelectedIds(): number[] {
        const value: number | number[] = this.hub.value;

        if (Array.isArray(value)) {
            return value;
        }

        return value === null || value === undefined ? [] : [value];
    }

    private setSelectedGroup(id: number | number[]): void {
        if (!Array.isArray(id)) {
            id = [id];
        }

        const ids: number[] = id;

        const selected_groups: { group: string, hubs: Warehouse.IHub[] }[] = [];

        for (const row of this.hubs) {
            const selectedHubs: Warehouse.IHub[] = row.hubs.filter((sHub: Warehouse.IHub): boolean => {
                return ids.includes(sHub.id);
            });

            if (selectedHubs.length) {
                selected_groups.push({
                    group: row.group.name, hubs: row.hubs.filter((sHub: Warehouse.IHub): boolean => {
                        return !ids.includes(sHub.id);
                    })
                });
            }
        }

        this.onGroupSelected.emit(selected_groups);
    }

    /**
     * Whether an input received a new value after its initial binding
     * (the initial value is already handled by ngOnInit)
     * @param change
     */
    private isUpdated(change: SimpleChange): boolean {
        return !!change && !change.firstChange;
    }

    /**
     * Replace the hub list with externally provided groups
     * @param options
     */
    private setGroupedOptions(options: { group: string, hubs: Warehouse.IHub[] }[]): void {
        if (!options) {
            return;
        }

        this.hubs = this.mapGroupedHubs(options);
        this.hub.setValue(this.value);
        this.applySearch(this.hubSearch.value);
        this.onGetHubs.emit(this.hubs);
    }

    public groupSelectAll(row: any, isSelected: boolean): void {
        const oldValues = this.hub.value || [];
        const groupHubsIds = row.hubs.map(item => item.id);

        if (isSelected) {
            const newValues = [...new Set([...oldValues, ...groupHubsIds])];
            this.hub.setValue(newValues);
        } else {
            const newValues = oldValues.filter(item1 => !groupHubsIds.includes(item1));
            this.hub.setValue(newValues);
        }
        // the whole group was just selected/deselected, so it is never partially selected here
        row.group.checked = isSelected;
        row.group.indeterminate = false;

        this.changeDetectorRef.markForCheck();
    }

    public checkSelectGroups(id: number | number[]): void {
        if (!Array.isArray(id)) {
            id = [id];
        }

        const ids: number[] = id;

        // const selected_groups: { group: IHubGroup, hubs: Warehouse.IHub[] }[] = [];

        for (const row of this.hubs) {
            const selectedCount: number = row.hubs.filter((sHub: Warehouse.IHub): boolean => {
                return ids.includes(sHub.id);
            }).length;

            // checked only when the whole group is selected, indeterminate while partially selected
            row.group.checked = selectedCount > 0 && selectedCount === row.hubs.length;
            row.group.indeterminate = selectedCount > 0 && selectedCount < row.hubs.length;
        }
        this.changeDetectorRef.markForCheck();
    }

    public allSelect(): void {
        const select: number[] = [];

        const search: string = this.hubSearch.value ? this.hubSearch.value : "";

        const filtered: { group: IHubGroup, hubs: Warehouse.IHub[] }[] = this.filterHubs(this.hubs, search);
        for (const list of filtered) {
            for (const hub of list.hubs) {
                select.push(hub.id);
            }
        }

        if (Array.isArray(this.hub.value) && select.length === this.hub.value.length) {
            this.hub.setValue([]);
        } else {
            this.hub.setValue(select);
        }

        this.changeDetectorRef.markForCheck();
    }

    /**
     * Get hub list (as select options)
     * @returns {Promise<any>}
     */
    public async getHubs(): Promise<any> {
        if (this.options_grouped) {
            this.hubs = this.mapGroupedHubs(this.options_grouped);
        } else if (this.options) {
            this.hubs = this.mapHubs(this.options);
        } else {
            this.spinnerService.show();

            const {data}: Api.IResponse = await this.apiService.request(Api.EMethod.Get, ["hub", this.url], null, {
                address_id: this.address_id,
                data_structure: "grouped",
                line_type: this.line_type,
                service_level_id: this.service_level_id,
                part_master_id: this.part_master_id,
                part_master_in_stock: this.part_master_in_stock,
                with_hidden: this.show_inactive,
                force_id: this.value ? this.value : null,
            });
            this.spinnerService.hide();
            if (data) {
                this.hubs = this.mapHubs(data);

            }
        }

        if (this.hubs) {
            this.applySearch(this.hubSearch.value);
            this.onGetHubs.emit(this.hubs);

            if (this.hub.value) {
                this.setSelectedGroup(this.hub.value);
            }
        }
    }

    /**
     * Get Hub name by id
     * @param id
     */
    public getHubNameById(id: number | number[]): string {
        if (!Array.isArray(id)) {
            id = [id];
        }
        const ids: number[] = id;
        const names: string[] = [];

        for (const row of this.hubs) {
            for (const hub of row.hubs) {
                if (ids.includes(hub.id)) {
                    names.push((hub.customers_inventory_name ? hub.customers_inventory_name + " " : "")
                        + (hub.customers_sub_inventory || "") + " (" + hub.gb + ") "
                        + (hub.distance ? " - " + hub.distance + "km" : "")
                        + (hub.is_active ? "" : " | INACTIVE")
                    );
                }
            }
        }
        return names.join(", ");
    }

    /**
     * Show hubs on google map
     */
    public async openMap(): Promise<any> {
        const response: Modal.IResponse = await this.modalService.open(HubsMapModalComponent, {
            hubs: this.hubs,
            modalHeight: 500,
            modalWidth: 1200,
            part_master_id: this.part_master_id
        });

        if (response && response.value) {
            this.hub.setValue(response.value.id);
            this.changeDetectorRef.markForCheck();
        }
    }

    public closed(): void {
        setTimeout((): void => {
            this.onClose.emit(this.hub.value);
        }, 10);
    }

    public ngOnInit(): void {
        this.getHubs();

        this.hub.setValue(this.value);

        this.changeDetectorRef.markForCheck();

        this.hubSearch.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(500))
            .subscribe((value: string): void => {
                this.applySearch(value);
            });

        this.hub.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(500), distinctUntilChanged())
            .subscribe((value: number): void => {
                this.onChange.emit(value);
                this.setSelectedGroup(value);
                this.checkSelectGroups(value);
                this.onValueName.emit(this.getHubNameById(value));
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.isUpdated(changes.value)) {
            this.hub.setValue(changes.value.currentValue);
            this.changeDetectorRef.markForCheck();
        }

        if (this.isUpdated(changes.options_grouped)) {
            this.setGroupedOptions(changes.options_grouped.currentValue);
        }

        const needsReload: boolean = HubSelectComponent.RELOAD_ON_CHANGE
            .some((input: string): boolean => this.isUpdated(changes[input]));

        if (needsReload) {
            this.getHubs();
        }
    }


}

